/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from 'vue';
import Router from 'vue-router';
import store from './store/store';
import ModuleServiceAppointmentReports from './store/service-appointments/reports/moduleServiceAppointmentReports';

const originalPush = Router.prototype.push;
Router.prototype.push = async function push (location, onComplete, onAbort) {
    if (onComplete || onAbort) {
        return originalPush.call(this, location, onComplete, onAbort);
    }

    try {
        return await originalPush.call(this, location);
    } catch (err) {
        if (Router.isNavigationFailure(err)) {
            return err;
        }

        return Promise.reject(err);
    }
}

Vue.use(Router);

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior (to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return false;
        }
    },
    routes: [
        {
            // =============================================================================
            // MAIN LAYOUT ROUTES
            // =============================================================================
            path: '',
            component: () => import('./layouts/main/Main.vue'),
            children: [
                // =============================================================================
                // Main Routes
                // =============================================================================
                {
                    path: '/',
                    name: 'home-page',
                    component: () => import('@/views/pages/home/HomePage.vue'),
                    meta: {
                        pageTitle: 'Home',
                        hidePageTitle: true,
                    },
                },
                /*{
                    path: '/',
                    name: 'dashboard',
                    component: () => import('@/views/pages/Dashboard.vue'),
                    meta: {
                        pageTitle: 'Dashboard',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                                active: true,
                            },
                        ],
                    },
                },*/
                {
                    path: '/shop-drawings/request-duration-management',
                    name: 'request-duration-management',
                    component: () => import('@/views/pages/shop-drawings/request-duration-management/RequestDurationManagementDashboard.vue'),
                    meta: {
                        pageTitle: 'Request Duration Management',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Shop Drawings',
                                url: '/shop-drawings',
                            },
                            {
                                title: 'Request Duration Management',
                                active: true,
                            },
                        ],
                    },
                    beforeEnter (to, from, next) {
                        next();
                    },
                    children: [
                        {
                            path: '/shop-drawings/request-duration-management/:id',
                            beforeEnter (to, from, next) {
                                let toId = null;
                                if (Number.isInteger(+to.params.id)) {
                                    toId = +to.params.id;
                                }
                                next();
                            },
                        },
                    ],
                },
                {
                    path: '/shop-drawings/cross-checking/checklist',
                    name: 'cross-checking-checklist',
                    component: () => import('@/views/pages/shop-drawings/cross-checking/CrossCheckingItemsDashboard.vue'),
                    meta: {
                        pageTitle: 'Checklist Settings Management',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Shop Drawings',
                                url: '/shop-drawings',
                            },
                            {
                                title: 'Checklist Settings',
                            },
                            {
                                title: 'Checklist Items Management',
                                active: true,
                            },
                        ],
                        hidePageTitle: true,
                    },
                    beforeEnter (to, from, next) {
                        next();
                    },
                    children: [
                        { path: '/shop-drawings/cross-checking/checklist/:id',
                            beforeEnter (to, from, next) {
                                let toId = null;
                                if (Number.isInteger(+to.params.id)) {
                                    toId = +to.params.id;
                                }
                                next();
                            },
                        },
                    ],
                },
                {
                    path: '/shop-drawings/cross-checking/classifications',
                    name: 'cross-checking-checklist',
                    component: () => import('@/views/pages/shop-drawings/cross-checking/CrossCheckingClassificationDashboard.vue'),
                    meta: {
                        pageTitle: 'Classifications Settings Management',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Shop Drawings',
                                url: '/shop-drawings',
                            },
                            {
                                title: 'Checklist Settings',
                            },
                            {
                                title: 'Classifications Settings Management',
                                active: true,
                            },
                        ],
                        hidePageTitle: true,
                    },
                    beforeEnter (to, from, next) {
                        next();
                    },
                    children: [
                        {
                            path: '/shop-drawings/cross-checking/classifications/:id',
                            beforeEnter (to, from, next) {
                                let toId = null;
                                if (Number.isInteger(+to.params.id)) {
                                    toId = +to.params.id;
                                }
                                next();
                            },
                        },
                    ],
                },
                {
                    path: '/shop-drawings/priority_settings_management',
                    name: 'priority-settings-management',
                    component: () => import('@/views/pages/shop-drawings/priority-settings-management/PrioritySettingsManagementDashboard.vue'),
                    meta: {
                        pageTitle: 'Priority Settings Management',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Shop Drawings',
                                url: '/shop-drawings',
                            },
                            {
                                title: 'Priority Settings Management',
                                active: true,
                            },
                        ],
                        hidePageTitle: true,
                    },
                    beforeEnter (to, from, next) {
                        next();
                    },
                    children: [
                        {
                            path: '/shop-drawings/priority-settings-management/:id',
                            beforeEnter (to, from, next) {
                                let toId = null;
                                if (Number.isInteger(+to.params.id)) {
                                    toId = +to.params.id;
                                }
                                next();
                            },
                        },
                    ],
                },
                {
                    path: '/shop-drawings',
                    name: 'shop-drawings',
                    component: () => import('@/views/pages/shop-drawings/ShopDrawingsDashboard.vue'),
                    meta: {
                        pageTitle: 'Shop Drawings',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Shop Drawings',
                                active: true,
                            },
                        ],
                    },
                },
                {
                    path: '/shop-drawings/:id',
                    name: 'shop-drawings-id',
                    component: () => import('@/views/pages/shop-drawings/ShopDrawings.vue'),
                    meta: {
                        pageTitle: 'Shop Drawings',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Shop Drawings',
                                url: '/shop-drawings',
                            },
                            {
                                title: 'Shop Drawings',
                                active: true,
                            },
                        ],
                        fullPageInner: true,
                    },
                },
                {
                    path: '/shop-drawings/:id/:revisionId',
                    name: 'shop-drawings-id-revision',
                    component: () => import('@/views/pages/shop-drawings/ShopDrawings.vue'),
                    meta: {
                        pageTitle: 'Shop Drawings',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Shop Drawings',
                                url: '/shop-drawings',
                            },
                            {
                                title: 'Shop Drawings',
                                active: true,
                            },
                        ],
                        fullPageInner: true,
                    },
                },
                {
                    path: '/service-appointments',
                    redirect: '/service-appointments/list',
                },
                {
                    path: '/service-appointments/list',
                    name: 'service-appointments-list',
                    component: () => import('@/views/pages/service-appointments/ServiceAppointmentsDashboard.vue'),
                    meta: {
                        pageTitle: 'Service Appointment List',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Field Service Management',
                                url: '/service-appointments',
                            },
                            {
                                title: 'Service Appointment List',
                                active: true,
                            },
                        ],
                        hidePageTitle: true,
                    },
                    beforeEnter (to, from, next) {
                        store.dispatch('serviceAppointments/setBaseRoute', '/service-appointments/list');
                        next();
                    },
                    children: [
                        {
                            path: ':id',
                            beforeEnter (to, from, next) {
                                let toId = null;
                                if (Number.isInteger(+to.params.id)) {
                                    toId = +to.params.id;
                                }
                                store.dispatch('serviceAppointments/openEditAppointmentModal', toId);
                                next();
                            },
                        },
                        {
                            path: 'report/:id',
                            beforeEnter (to, from, next) {
                                if (!store.hasModule(['serviceAppointments', 'reportModal'])) {
                                    store.registerModule(['serviceAppointments', 'reportModal'],
                                        ModuleServiceAppointmentReports);
                                }
                                let toId = null;
                                if (Number.isInteger(+to.params.id)) {
                                    toId = +to.params.id;
                                }
                                router.app.$nextTick(() => {
                                    store.dispatch('serviceAppointments/reportModal/openReportModal', toId);
                                });
                                next();
                            },
                        },
                    ],
                },
                {
                    path: '/factory-tests',
                    redirect: '/factory-tests/list',
                },
                {
                    path: '/factory-tests/list',
                    name: 'factory-tests',
                    component: () => import('@/views/pages/factory-tests/FactoryTestsDashboard.vue'),
                    meta: {
                        pageTitle: 'Factory Tests',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Factory Tests',
                                url: '/factory-tests',
                            },
                            {
                                title: 'Factory Tests List',
                                active: true,
                            },
                        ],
                        hidePageTitle: true,
                    },
                    beforeEnter (to, from, next) {
                        store.dispatch('factoryTests/setBaseRoute', '/factory-tests/list');
                        next();
                    },
                    children: [
                        {
                            path: ':id',
                            beforeEnter (to, from, next) {
                                let toId = null;
                                if (Number.isInteger(+to.params.id)) {
                                    toId = +to.params.id;
                                }
                                store.dispatch('factoryTests/openFactoryTestModal', toId);
                                next();
                            },
                        },
                    ],
                },
                {
                    path: '/factory-tests/testing-subjects',
                    name: 'factory-tests-testing-subjects',
                    component: () => import('@/views/pages/factory-tests/testing-subjects/TestingSubjectsDashboard.vue'),
                    meta: {
                        pageTitle: 'Testing Subjects',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Factory Tests',
                                url: '/factory-tests',
                            },
                            {
                                title: 'Testing Subjects',
                                active: true,
                            },
                        ],
                        hidePageTitle: true,
                    },
                    beforeEnter (to, from, next) {
                        store.dispatch('factoryTests/setBaseRoute', '/factory-tests/testing-subjects');
                        next();
                    },
                },
                {
                    path: '/factory-tests/quality-assurance/list',
                    name: 'quality-assurance',
                    component: () => import('@/views/pages/quality-assurance/Dashboard.vue'),
                    meta: {
                        pageTitle: 'Quality Assurance',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Factory Tests',
                                url: '/factory-tests',
                            },
                            {
                                title: 'Quality Assurance List',
                                active: true,
                            },
                        ],
                        hidePageTitle: true,
                    },
                    children: [
                        {
                            path: ':id',
                            beforeEnter (to, from, next) {
                                let toId = null;
                                if (Number.isInteger(+to.params.id)) {
                                    toId = +to.params.id;
                                }
                                store.dispatch('qualityAssurance/openModal', {id: toId});
                                next();
                            },
                        },
                    ],
                },
                {
                    path: '/service-appointments/calendar',
                    name: 'service-appointments-calendar',
                    component: () => import('@/views/pages/service-appointments/calendar/ServiceAppointmentsCalendarPage.vue'),
                    meta: {
                        pageTitle: 'Service Calendar',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Field Service Management',
                                url: '/service-appointments',
                            },
                            {
                                title: 'Service Calendar',
                                active: true,
                            },
                        ],
                        hidePageTitle: true,
                    },
                    beforeEnter (to, from, next) {
                        store.dispatch('serviceAppointments/setBaseRoute', '/service-appointments/calendar');
                        next();
                    },
                    children: [
                        {
                            path: ':id',
                            beforeEnter (to, from, next) {
                                let toId = null;
                                if (Number.isInteger(+to.params.id)) {
                                    toId = +to.params.id;
                                }
                                store.dispatch('serviceAppointments/openEditAppointmentModal', toId);
                                next();
                            },
                        },
                        {
                            path: 'report/:id',
                            beforeEnter (to, from, next) {
                                if (!store.hasModule(['serviceAppointments', 'reportModal'])) {
                                    store.registerModule(['serviceAppointments', 'reportModal'],
                                        ModuleServiceAppointmentReports);
                                }
                                let toId = null;
                                if (Number.isInteger(+to.params.id)) {
                                    toId = +to.params.id;
                                }
                                router.app.$nextTick(() => {
                                    store.dispatch('serviceAppointments/reportModal/openReportModal', toId);
                                });
                                next();
                            },
                        },
                    ],
                },
                {
                    path: '/tests/qr-reader',
                    name: 'tests-qr-reader',
                    component: () => import('@/views/pages/tests/QrReaderTest.vue'),
                    meta: {},
                },
                /*{
                    path: '/service-appointments/reports',
                    name: 'service-appointments-reports',
                    component: () => import('@/views/pages/service-appointments/calendar/ServiceAppointmentsCalendarPage.vue'),
                    meta: {
                        pageTitle: 'Service Reports',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Field Service Management',
                                url: '/service-appointments',
                            },
                            {
                                title: 'Service Reports',
                                active: true,
                            },
                        ],
                        hidePageTitle: true,
                    },
                },*/
                {
                    path: '/admin',
                    redirect: '/admin/synchronizations',
                },
                {
                    path: '/admin/synchronizations',
                    name: 'admin-synchronizations',
                    component: () => import('@/views/pages/admin/Synchronizations.vue'),
                    meta: {
                        pageTitle: 'Synchronizations',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Administration',
                                url: '/admin',
                            },
                            {
                                title: 'Synchronizations',
                                active: true,
                            },
                        ],
                        hidePageTitle: false,
                    },
                },
                {
                    path: '/admin',
                    redirect: '/admin/impersonation',
                },
                {
                    path: '/admin/impersonation',
                    name: 'admin-auth-users',
                    component: () => import('@/views/pages/admin/AuthUsers.vue'),
                    meta: {
                        pageTitle: 'Users',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Administration',
                                url: '/admin',
                            },
                            {
                                title: 'Users',
                                active: true,
                            },
                        ],
                        hidePageTitle: false,
                    },
                },
                {
                    path: '/admin/maps-tests',
                    name: 'maps-tests',
                    component: () => import('@/views/pages/admin/MapsTests.vue'),
                    meta: {
                        pageTitle: 'Maps Tests',
                        breadcrumb: [
                            {
                                title: 'Home',
                                url: '/',
                            },
                            {
                                title: 'Administration',
                                url: '/admin',
                            },
                            {
                                title: 'Maps Tests',
                                active: true,
                            },
                        ],
                        hidePageTitle: false,
                    },
                },
            ],
        },
        // =============================================================================
        // FULL PAGE LAYOUTS
        // =============================================================================
        {
            path: '',
            component: () => import('@/layouts/full-page/FullPage.vue'),
            children: [
                // =============================================================================
                // PAGES
                // =============================================================================
                {
                    path: '/login/callback',
                    name: 'page-login-auth-callback',
                    component: () => import('@/views/Callback.vue'),
                    meta: {
                        authNotRequired: true,
                    },
                },
                {
                    path: '/login/oauth2',
                    name: 'page-oauth2-login-auth-callback',
                    component: () => import('@/views/Oauth2.vue'),
                    meta: {
                        authNotRequired: true,
                    },
                },
                {
                    path: '/login',
                    name: 'page-login',
                    component: () => import('@/views/pages/login/Login.vue'),
                    meta: {
                        authNotRequired: true,
                    },
                },
                {
                    path: '/pages/register',
                    name: 'page-register',
                    component: () => import('@/views/pages/register/Register.vue'),
                    meta: {
                        authNotRequired: true,
                    },
                },
                {
                    path: '/pages/forgot-password',
                    name: 'page-forgot-password',
                    component: () => import('@/views/pages/ForgotPassword.vue'),
                    meta: {
                        authNotRequired: true,
                    },
                },
                {
                    path: '/pages/reset-password',
                    name: 'page-reset-password',
                    component: () => import('@/views/pages/ResetPassword.vue'),
                    meta: {
                        authNotRequired: true,
                    },
                },
                {
                    path: '/pages/lock-screen',
                    name: 'page-lock-screen',
                    component: () => import('@/views/pages/LockScreen.vue'),
                    meta: {
                        authNotRequired: true,
                    },
                },
                {
                    path: '/not-found',
                    name: 'page-not-found',
                    component: () => import('@/views/pages/Error404.vue'),
                    meta: {
                        authNotRequired: true,
                    },
                },
                {
                    path: '/system-error',
                    name: 'page-system-error',
                    component: () => import('@/views/pages/Error500.vue'),
                    meta: {
                        authNotRequired: true,
                    },
                },
                {
                    path: '/pages/not-authorized',
                    name: 'page-not-authorized',
                    component: () => import('@/views/pages/NotAuthorized.vue'),
                    meta: {
                        authNotRequired: true,
                    },
                },
                {
                    path: '/access-denied',
                    name: 'page-access-denied',
                    component: () => import('@/views/pages/AccessDenied.vue'),
                    meta: {
                        pageTitle: 'Access Denied',
                    },
                },
                {
                    path: '/pages/maintenance',
                    name: 'page-maintenance',
                    component: () => import('@/views/pages/Maintenance.vue'),
                    meta: {
                        authNotRequired: true,
                    },
                },
            ],
        },
        // =============================================================================
        // FRAME PAGE LAYOUTS
        // =============================================================================
        {
            path: '',
            component: () => import('@/layouts/frame/Frame.vue'),
            children: [
                {
                    path: '/frame/service-appointments/edit-modal',
                    name: 'frame-service-appointment-edit-modal',
                    component: () => import('@/views/pages/service-appointments/ServiceAppointmentFrameEditModal.vue'),
                    meta: {},
                },
                {
                    path: '/frame/service-appointments/report-modal',
                    name: 'frame-service-appointment-report-modal',
                    component: () => import('@/views/pages/service-appointments/report/ServiceAppointmentFrameReportModal.vue'),
                    meta: {},
                },
            ],
        },
        // Redirect to 404 page, if no match found
        {
            path: '*',
            redirect: '/not-found',
        },
    ],
});

router.afterEach(() => {
    // Remove initial loading
    const appLoading = document.getElementById('loading-bg');
    if (appLoading) {
        appLoading.style.display = 'none';
    }
});

router.beforeEach((to, from, next) => {
    // if (
    //     to.path === "/login" ||
    //     to.path === "/pages/forgot-password" ||
    //     to.path === "/not-found" ||
    //     to.path === "/system-error" ||
    //     to.path === "/pages/register" ||
    //     to.path === "/callback" ||
    //     (auth.isAuthenticated() || firebaseCurrentUser)
    // ) {
    //     return next();
    // }

    // Handles page title changes
    if (typeof store.state.AppInfo.name !== 'undefined') {
        if (typeof to.meta.pageTitle !== 'undefined') {
            document.title = to.meta.pageTitle + ' | ' + store.state.AppInfo.name;
        } else {
            document.title = store.state.AppInfo.name;
        }
    }

    // If auth required, check login. If login fails redirect to login page
    if (!to.meta.authNotRequired) {
        if (!store.state.auth.isUserLoggedIn()) {
            if (typeof store.state.AppInfo.auth !== 'undefined' && store.state.AppInfo.auth.login_url.indexOf(
                'http') !== -1) {
                if (window.parent) {
                    window.parent.postMessage('Unauthorized', '*');
                }

                localStorage.setItem('returnUrl', to.fullPath);

                window.location.href = store.state.AppInfo.auth.login_url;

                return;
            } else {
                router.push({
                    path: store.state.AppInfo.auth.login_url,
                    query: { to: to.path },
                });
            }
        }
    }

    return next();
    // Specify the current path as the customState parameter, meaning it
    // will be returned to the application after auth
    // auth.login({ target: to.path });
});

export default router;
